import { useContext, useEffect, useRef, useState } from 'react';
import { MainContext } from '../contexts/main';
import { checkPaymentMethod, confirmRenewalRetry, createPaymentMethod, getBilling, getPendingRenewal, retryRenewal } from '../helpers/api';
import { markErrorNewPaymentMethodFields, validateNewPaymentMethod } from '../helpers/paymentMethods';
import { renewalErrorText } from '../helpers/renewal';
import { getText } from '@educaplay/app-core/language';
import { PAYMENT_STATUS } from '../helpers/constants';
import config from '../resources/config.json';

export const useRenewalRetry = ({ payerMode }) => {

    const { environment, mainReducer, appReducer } = useContext( MainContext );

    const returningFromGateway = (environment.renewalCheck && (environment.renewalPaymentIntent != '')) || (environment.renewalMethod != '');

    const [ stateStatus, setStateStatus ] = useState(returningFromGateway ? 'checking' : 'idle');
    const [ stateResult, setStateResult ] = useState(false);
    const [ stateChoosing, setStateChoosing ] = useState(false);

    const mounted = useRef(true);
    const pendingSnapshot = useRef(mainReducer.state.pendingRenewal);

    useEffect(() => {
        mounted.current = true;
        if (returningFromGateway) {
            window.history.replaceState(null, '', window.location.pathname);
            if (environment.renewalMethod != '') {
                waitForPaymentMethod(environment.renewalMethod);
            } else {
                confirm(environment.renewalPaymentIntent);
            }
        }
        return () => {
            mounted.current = false;
        };
    }, []);

    const notifyError = (content) => {
        appReducer.dispatch({
            type: 'setNotifications',
            payload: [{
                type: 'error',
                content: content
            }]
        });
    }

    const refresh = async () => {
        if (payerMode) {
            const response = await getPendingRenewal({ environment: environment.entorno });
            mainReducer.dispatch({
                type: 'setPendingRenewal',
                payload: response.pendingRenewal ?? false
            });
        } else {
            const billingData = await getBilling({
                tokenID: environment.tokenID,
                environment: environment.entorno,
                budget: ''
            });
            if (billingData.status !== 'error') {
                mainReducer.dispatch({
                    type: 'setBillingData',
                    payload: billingData,
                    environment: environment
                });
                mainReducer.dispatch({
                    type: 'setInvoices',
                    payload: false
                });
            }
        }
    }

    const finish = async (result) => {
        setStateResult(result);
        setStateChoosing(false);
        await refresh();
        if (mounted.current) {
            setStateStatus('idle');
        }
    }

    const handleResponse = (response) => {
        if (!mounted.current) {
            return;
        }
        switch (response.status) {
        case 'success':
            mainReducer.dispatch({ type: 'setRenewalInvoice', payload: response.invoice ?? false });
            finish({ type: 'ok', pendingRenewal: pendingSnapshot.current, end: response.end });
            break;
        case 'declined':
            finish({ type: 'ko', pendingRenewal: pendingSnapshot.current, decline: response.decline });
            break;
        case 'action':
            setStateStatus('redirecting');
            window.location.href = response.action;
            break;
        case 'processing':
            setStateStatus('checking');
            setTimeout(() => confirm(response.paymentIntent), config.renewal.pollMilliseconds);
            break;
        default:
            if (response.errors?.[0] == 'renewal_running') {
                setStateStatus('checking');
                setTimeout(recheckAfterRunningRenewal, config.renewal.runningRecheckMilliseconds);
                break;
            }
            setStateStatus('idle');
            notifyError(renewalErrorText(response.errors));
        }
    }

    const recheckAfterRunningRenewal = async () => {
        if (!mounted.current) {
            return;
        }
        await refresh();
        if (mounted.current) {
            setStateStatus('idle');
        }
    }

    const confirm = async (paymentIntent) => {
        if (mounted.current) {
            handleResponse(await confirmRenewalRetry({ environment: environment.entorno, paymentIntent: paymentIntent }));
        }
    }

    const retry = async (paymentMethodId) => {
        appReducer.dispatch({ type: 'clearNotifications' });
        pendingSnapshot.current = mainReducer.state.pendingRenewal;
        setStateStatus('processing');
        handleResponse(await retryRenewal({ environment: environment.entorno, lang: environment.lang, paymentMethod: paymentMethodId }));
    }

    const waitForPaymentMethod = async (paymentMethodId) => {
        const paymentMethod = await checkPaymentMethod({ environment: environment.entorno, paymentMethod: paymentMethodId });
        if (!mounted.current) {
            return;
        }
        if ((paymentMethod === false) || (paymentMethod.status === false) || (paymentMethod.status == PAYMENT_STATUS.PENDING)) {
            setTimeout(() => waitForPaymentMethod(paymentMethodId), config.renewal.pollMilliseconds);
        } else if (paymentMethod.status == PAYMENT_STATUS.SUCCESS) {
            retry(paymentMethodId);
        } else {
            setStateStatus('idle');
            notifyError(getText('notification_payment_payment_could_not_be_made'));
        }
    }

    const retryWithSelection = async () => {
        if (!mainReducer.state.acceptedConditions) {
            notifyError(getText('notification_conditions_not_accepted'));
            return;
        }
        const newPaymentMethod = mainReducer.state.newPaymentMethod;
        if (newPaymentMethod === false) {
            if (mainReducer.state.selectedPaymentMethod === false) {
                notifyError(getText('notification_select_payment_method'));
            } else {
                retry(mainReducer.state.selectedPaymentMethod);
            }
            return;
        }
        if (newPaymentMethod.type === false) {
            notifyError(getText('notification_select_payment_method'));
            return;
        }
        const errors = validateNewPaymentMethod(newPaymentMethod.type, newPaymentMethod.data);
        if (errors.length > 0) {
            appReducer.dispatch({ type: 'setFieldErrors', payload: errors });
            notifyError(getText('notification_bad_payment'));
            return;
        }
        appReducer.dispatch({ type: 'clearFieldErrors' });
        appReducer.dispatch({ type: 'clearNotifications' });
        setStateStatus('processing');
        const pendingRenewal = mainReducer.state.pendingRenewal;
        const response = await createPaymentMethod({
            environment: environment.entorno,
            environmentCookies: environment.entornoCookies,
            lang: environment.lang,
            email: payerMode ? pendingRenewal.paymentMethod.payer : mainReducer.state.realUser.email,
            budget: pendingRenewal.budget,
            return: 'renewal',
            showConsent: environment.showConsent,
            paymentMethod: newPaymentMethod
        });
        switch (response.status) {
        case PAYMENT_STATUS.SUCCESS:
            waitForPaymentMethod(response.id);
            break;
        case PAYMENT_STATUS.PENDING:
            setStateStatus('redirecting');
            window.location.href = response.action;
            break;
        default:
            setStateStatus('idle');
            if ((response.errors ?? []).includes('payment_method_creation_blocked')) {
                notifyError(getText('notification_payment_method_creation_blocked'));
            } else {
                notifyError(getText('notification_bad_payment'));
                const fieldErrors = markErrorNewPaymentMethodFields(newPaymentMethod.type);
                if (fieldErrors.length > 0) {
                    appReducer.dispatch({ type: 'setFieldErrors', payload: fieldErrors });
                }
            }
        }
    }

    const dismiss = () => {
        setStateResult(false);
    }

    const choose = () => {
        appReducer.dispatch({ type: 'clearNotifications' });
        setStateResult(false);
        setStateChoosing(true);
    }

    const cancelChoosing = () => {
        appReducer.dispatch({ type: 'clearNotifications' });
        appReducer.dispatch({ type: 'clearFieldErrors' });
        mainReducer.dispatch({ type: 'changeNewPaymentMethod', payload: false });
        if (mainReducer.state.currentPaymentMethod !== false) {
            mainReducer.dispatch({ type: 'changePaymentMethod', payload: mainReducer.state.currentPaymentMethod.id });
        }
        setStateChoosing(false);
    }

    return {
        status: stateStatus,
        result: stateResult,
        choosing: stateChoosing,
        retry: retry,
        retryWithSelection: retryWithSelection,
        choose: choose,
        dismiss: dismiss,
        cancelChoosing: cancelChoosing
    };
}
